import { type CreateInboxInvoiceDto } from "@finway-group/shared/lib/models"
import { type ActionCreator, type Dispatch } from "redux"
import { getTableFilterQueryStringStore, getTableSearchQueryStringStore } from "~Shared/hooks/table.hooks"
import { type InboxDocument } from "~Shared/models/inboxDocument"
import { InboxInvoiceService } from "~Shared/services/inboxInvoice.service"
import { type ThunkResult } from "~Shared/store"
import {
    type DeleteManyInboxInvoicesAction,
    type DiscardInboxInvoiceAction,
    type FetchAllInboxInvoicesAction,
    type FetchInboxInvoicesCountAction,
    InboxInvoiceActionTypes,
    type MoveOneInboxInvoiceAction,
    type NewInboxInvoiceAction,
    type SetShouldRefetchInboxAction,
    type SyncOneInboxInvoiceAction,
} from "~Shared/store/actions/inboxInvoice/inboxInvoiceTypes"
import { refetchInboxInvoiceTables, refetchTable } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"

export const fetchAllInboxInvoices =
    (append = false) =>
    async (dispatch: Dispatch) => {
        const { inboxInvoices, totalPages, totalDocs } = await InboxInvoiceService.fetchAllInboxInvoices({ limit: 20 })

        const fetchAllInboxInvoicesAction: FetchAllInboxInvoicesAction = {
            type: InboxInvoiceActionTypes.FETCH_ALL_INBOX_INVOICES,
            inboxInvoices,
            totalPages,
            totalDocs,
            append,
        }
        dispatch(fetchAllInboxInvoicesAction)
        setShouldRefetchInbox(false)(dispatch)
        return inboxInvoices
    }

export const fetchDashboardInboxInvoicesCount: ActionCreator<ThunkResult<void>> = () => async (dispatch: Dispatch) => {
    const filterQueryString = getTableFilterQueryStringStore(TablesEnum.INBOX_INVOICE)
    const searchQueryString = getTableSearchQueryStringStore(TablesEnum.INBOX_INVOICE)

    const count = await InboxInvoiceService.fetchInboxInvoicesCount(filterQueryString + searchQueryString)

    const fetchInboxInvoicesCount: FetchInboxInvoicesCountAction = {
        type: InboxInvoiceActionTypes.FETCH_INBOX_INVOICES_COUNT,
        count,
    }

    dispatch(fetchInboxInvoicesCount)
    return count
}

export const createInboxInvoice = (createInboxInvoice: CreateInboxInvoiceDto) => async (dispatch: Dispatch<any>) => {
    const inboxInvoice = await InboxInvoiceService.createInboxInvoice(createInboxInvoice)
    const newInboxInvoiceAction: NewInboxInvoiceAction = {
        type: InboxInvoiceActionTypes.NEW_INBOX_INVOICE,
        inboxInvoice,
    }

    dispatch(newInboxInvoiceAction)
    setShouldRefetchInbox(true)(dispatch)
    dispatch(refetchTable(TablesEnum.INBOX_INVOICE))
    dispatch(fetchDashboardInboxInvoicesCount())
    return inboxInvoice
}

export const discardInboxInvoice = (discardedInboxInvoice: InboxDocument) => async (dispatch: Dispatch<any>) => {
    await InboxInvoiceService.discardInboxInvoice(discardedInboxInvoice._id)
    const discardInboxInvoiceAction: DiscardInboxInvoiceAction = {
        type: InboxInvoiceActionTypes.DISCARD_INBOX_INVOICE,
        inboxInvoice: discardedInboxInvoice,
    }

    dispatch(discardInboxInvoiceAction)
    dispatch(fetchDashboardInboxInvoicesCount())
    dispatch(refetchInboxInvoiceTables())

    // refetches the table
    setShouldRefetchInbox(true)(dispatch)
}

export const deleteManyInboxInvoices = (ids: Array<string>) => async (dispatch: Dispatch<any>) => {
    await InboxInvoiceService.deleteManyInboxInvoices(ids)
    const deleteManyInboxInvoicesAction: DeleteManyInboxInvoicesAction = {
        type: InboxInvoiceActionTypes.DELETE_MANY_INBOX_INVOICES,
        inboxInvoiceIds: ids,
    }

    dispatch(deleteManyInboxInvoicesAction)
    dispatch(fetchDashboardInboxInvoicesCount())
    dispatch(refetchInboxInvoiceTables())

    // refetches the table
    setShouldRefetchInbox(true)(dispatch)
}

export const syncInvoiceInbox = (id: string) => async (dispatch: Dispatch) => {
    const inboxInvoice = await InboxInvoiceService.fetchOneInboxInvoice(id)
    const syncOneInboxInvoiceAction: SyncOneInboxInvoiceAction = {
        type: InboxInvoiceActionTypes.SYNC_ONE_INBOX_INVOICE,
        inboxInvoice,
    }

    dispatch(syncOneInboxInvoiceAction)

    return inboxInvoice
}

export const setShouldRefetchInbox = (shouldRefetch: boolean) => async (dispatch: Dispatch) => {
    const setShouldRefetchAction: SetShouldRefetchInboxAction = {
        type: InboxInvoiceActionTypes.SET_SHOULD_REFETCH_INBOX,
        shouldRefetch,
    }

    dispatch(setShouldRefetchAction)
}

export const moveOneInboxInvoice = (id: string, companyId: string) => async (dispatch: Dispatch) => {
    const data = await InboxInvoiceService.moveInboxInvoice(id, companyId)

    const moveOneInvoiceInboxAction: MoveOneInboxInvoiceAction = {
        type: InboxInvoiceActionTypes.MOVE_ONE_INBOX_INVOICE,
        inboxInvoiceId: id,
    }

    dispatch(moveOneInvoiceInboxAction)

    return data
}
